import { ChevronDownIcon, ChevronUpIcon } from "../../../../automationStudioIcons";
import { useMemo, useState } from "react";
import { Selectbox } from "../../../../../FormComponent";
import SmartInputBox from "../../../shared/SmartInputBox";
import SelectorsTab from "./SelectorsTab";
import InfoBox from "../../../shared/InfoBox";
import { EXECUTION_MODES, toolSupportsElements } from '../../../../../AutomationStudio/constant';
import { LABELS } from "../../../../content";
import styles from "../../../../../../styles/automationStudio/automationStudio.module.scss";

/**
 * ToolModeTab - Tool selection and execution mode configuration
 * When mcpToolsList is available, tool is editable for all steps (mirrors Script Manager).
 */
export default function ToolModeTab({
    step,
    onStepChange,
    isExecuting = false,
    readOnlyForMetaInfo = false,
    mcpToolsList = [],
    mcpToolMap = {},
    executionStatus = null,
    executionErrorMessage = null,
    executedCodeStrategyType = null,
    fileSuggestions = [],
    fileDescriptions = {},
    variablesList = [],
    variablesListDescriptions = {},
    selectorsTabProps = {},
    fieldDirtyMap = null,
}) {
    const [codeExpanded, setCodeExpanded] = useState(true);
    const resolvedFileSuggestions = fileSuggestions;

    const editorLocked = isExecuting || readOnlyForMetaInfo;

    const strategies = Array.isArray(step.executionStrategies) ? step.executionStrategies : [];
    const playwrightCodeStrategy = strategies.find((s) => s.type === "playwright_code");

    const handleCodeChange = (newCode) => {
        const hasEntry = strategies.some(s => s.type === 'playwright_code');
        const updated = hasEntry
            ? strategies.map(s => s.type === 'playwright_code' ? { ...s, code: newCode } : s)
            : [...strategies, { type: 'playwright_code', code: newCode, priority: 0, enabled: true }];
        onStepChange({ executionStrategies: updated });
    };

    const currentTool = step.tool || '';

    const toolOptions = useMemo(() => {
        const baseOptions = Array.isArray(mcpToolsList)
            ? mcpToolsList.map((t) => ({ value: t.name, label: t.name }))
            : [];

        if (currentTool && !baseOptions.some((opt) => opt.value === currentTool)) {
            return [{ value: currentTool, label: currentTool }, ...baseOptions];
        }

        return baseOptions;
    }, [mcpToolsList, currentTool]);

    const toolError = step.toolError || '';

    const handleToolChange = (e) => {
        if (editorLocked) return;
        const newTool = (e && typeof e === 'object' && e.target && e.target.value !== undefined)
            ? e.target.value
            : (typeof e === 'string' ? e : '');

        if (!newTool) {
            onStepChange({ tool: '', args: {} });
            return;
        }

        const schema = mcpToolMap && mcpToolMap[newTool];
        const defaultArgs = {};
        if (schema && typeof schema === 'object') {
            Object.keys(schema).forEach(key => {
                const prop = schema[key];
                if (prop && typeof prop === 'object' && prop.type === 'boolean') {
                    defaultArgs[key] = false;
                } else if (prop && typeof prop === 'object' && prop.type === 'array') {
                    defaultArgs[key] = [];
                } else {
                    defaultArgs[key] = '';
                }
            });
        }
        const oldArgs = step.args ?? {};
        if (oldArgs.ref !== undefined) defaultArgs.ref = oldArgs.ref;
        if (oldArgs.startRef !== undefined) defaultArgs.startRef = oldArgs.startRef;
        if (oldArgs.endRef !== undefined) defaultArgs.endRef = oldArgs.endRef;

        onStepChange({ tool: newTool, args: defaultArgs });
    };

    const supportsElements = toolSupportsElements(currentTool);

    const handleModeChange = (e) => {
        if (editorLocked) return;
        onStepChange({ executionMode: e.target.value });
    };

    const executionMode = step.executionMode || EXECUTION_MODES.SMART;

    const playwrightCode =
        playwrightCodeStrategy?.code ??
        step.playwrightCode ??
        step.details?.playwrightCode ??
        step.executionGroup?.mainAction?.details?.playwrightCode ??
        `// Playwright code for ${step.tool}\n// Code will be available after execution`;

    const executedCodeLabel =
        executedCodeStrategyType === "playwright_code"
            ? "Execution Mode Code"
            : executedCodeStrategyType;

    const showExecutionError = executionStatus === "failed" && (executionErrorMessage || step?.error);
    const errorText =
        executionErrorMessage ||
        step?.error?.message ||
        step?.error ||
        "Failed, but execution service did not provide an error message yet (not handled).";
    const errorTextString =
        typeof errorText === "string" ? errorText : (errorText?.message ?? String(errorText));
    const errorDescription = executedCodeLabel
        ? `${errorTextString}\nExecuted strategy: ${executedCodeLabel}`
        : errorTextString;

    const toolSelect = (
        <Selectbox
            name="toolUsed"
            value={currentTool}
            placeholder="Select Tool Used"
            options={toolOptions}
            onChange={handleToolChange}
            size="small"
            error={toolError}
            disabled={editorLocked || !step.customStep}
        />
    );

    return (
        <div className={styles.toolModeTabContainer}>
            <div className={styles.formSection}>
                <span className={styles.toolLabel}>
                    Tool Used{step.customStep ? ' *' : ''}:
                </span>
                {fieldDirtyMap?.tool
                    ? <div className={styles.dirtyFieldTarget}>{toolSelect}</div>
                    : toolSelect}
            </div>

            {step.customStep && (
                <div className={styles.formSection}>
                    <InfoBox
                        type="info"
                        title="XPath-Only Mode (Custom Step)"
                        description="This custom step doesn't have AI-generated code. Configure XPath selectors below to execute this step."
                    />
                    <span className={styles.helperText} style={{ display: "block", marginTop: 8 }}>
                        <strong>Note:</strong> Only XPath selectors will run (AI generated code will be skipped)
                    </span>
                </div>
            )}

            {showExecutionError && (
                <div className={styles.formSection}>
                    <InfoBox
                        type="error"
                        title="Execution Failed"
                        description={errorDescription}
                    />
                </div>
            )}

            {supportsElements && !step.customStep && (
                <div className={styles.formSection}>
                    <h3 className={styles.sectionLabel}>
                        Execution Strategy
                    </h3>
                    <span className={styles.sectionDescription}>
                        Choose how this step should be executed during test runs
                    </span>
                    <div
                        role="radiogroup"
                        className={`${styles.radioGroup}${fieldDirtyMap?.executionMode ? ` ${styles.dirtyRadioGroup}` : ''}`}
                        style={editorLocked ? { opacity: 0.7, pointerEvents: "none" } : undefined}
                    >
                        <label
                            className={`${styles.radioCard} ${executionMode === EXECUTION_MODES.SMART ? styles.selected : ""}`}
                        >
                            <div className={styles.radioRow}>
                                <input
                                    type="radio"
                                    name={`execution-mode-${step?.id ?? "step"}`}
                                    value={EXECUTION_MODES.SMART}
                                    checked={executionMode === EXECUTION_MODES.SMART}
                                    onChange={handleModeChange}
                                    className={styles.radioNative}
                                    disabled={editorLocked}
                                />
                                <div className={styles.radioLabelContent}>
                                    <span className={styles.radioTitle}>
                                        Smart Mode (Recommended)
                                    </span>
                                    <span className={styles.radioDescription}>
                                        Executes AI-generated code first, automatically falls back to XPath selectors if needed
                                    </span>
                                </div>
                            </div>
                        </label>
                        <label
                            className={`${styles.radioCard} ${executionMode === EXECUTION_MODES.XPATH_ONLY ? styles.selected : ""}`}
                        >
                            <div className={styles.radioRow}>
                                <input
                                    type="radio"
                                    name={`execution-mode-${step?.id ?? "step"}`}
                                    value={EXECUTION_MODES.XPATH_ONLY}
                                    checked={executionMode === EXECUTION_MODES.XPATH_ONLY}
                                    onChange={handleModeChange}
                                    className={styles.radioNative}
                                    disabled={editorLocked}
                                />
                                <div className={styles.radioLabelContent}>
                                    <span className={styles.radioTitle}>
                                        XPath Only
                                    </span>
                                    <span className={styles.radioDescription}>
                                        Skip AI code and use XPath selectors directly for more predictable execution
                                    </span>
                                </div>
                            </div>
                        </label>
                    </div>
                </div>
            )}

            {supportsElements && !step.customStep && executionMode === EXECUTION_MODES.SMART && (
                <div className={styles.formSection}>
                    <div
                        className={styles.collapsibleHeader}
                        onClick={() => setCodeExpanded(!codeExpanded)}
                    >
                        <div>
                            <h3 className={styles.sectionLabel}>
                                AI-Generated Code
                            </h3>
                            <span className={styles.helperText}>
                                Primary execution strategy for this step
                            </span>
                        </div>
                        <button
                            type="button"
                            className={`${styles.asIconButton} ${styles.asIconButtonSm}`}
                            aria-expanded={codeExpanded}
                            aria-label={codeExpanded ? "Collapse code" : "Expand code"}
                        >
                            {codeExpanded ? (
                                <ChevronUpIcon style={{ width: '18px', height: '18px' }} />
                            ) : (
                                <ChevronDownIcon style={{ width: '18px', height: '18px' }} />
                            )}
                        </button>
                    </div>

                    <div className={`${styles.asCollapse} ${codeExpanded ? styles.asCollapseOpen : ""}`}>
                        <div className={`${styles.codeContainer}${fieldDirtyMap?.playwrightCode ? ` ${styles.dirtyFieldTarget}` : ''}`}>
                        <SmartInputBox
                            size="small"
                            value={playwrightCode}
                            onChange={(e) => handleCodeChange(e.target.value)}
                            placeholder={LABELS.STEP_DESCRIPTION_PLACEHOLDER}
                            suggestions={variablesList}
                            descriptions={variablesListDescriptions}
                            fileTrigger="@"
                            fileSuggestions={resolvedFileSuggestions}
                            fileDescriptions={fileDescriptions}
                            formatFileInsertion={(file) => `@[${file.id}]`}
                            disabled={editorLocked}
                            customClassName={styles.codeBlock}
                        />
                        </div>
                    </div>
                </div>
            )}

            {supportsElements && !step.customStep && (
                <SelectorsTab
                    step={step}
                    onStepChange={onStepChange}
                    isExecuting={isExecuting}
                    readOnlyForMetaInfo={readOnlyForMetaInfo}
                    fileSuggestions={resolvedFileSuggestions}
                    fileDescriptions={fileDescriptions}
                    variablesList={variablesList}
                    variablesListDescriptions={variablesListDescriptions}
                    strategyDirtyById={fieldDirtyMap?.strategyDirtyById ?? null}
                    embedded
                />
            )}
        </div>
    );
}
